/**
 * Brand Cart Drawer — Global Component
 * Handles displaying a single vendor's cart from anywhere in the site.
 * Provides consistent add-to-cart feedback and vendor-specific checkout.
 */

// Storefront-throttle aware fetch. Shopify's /cart.js, /cart/change.js and
// /cart/add.js all share a per-session rate limit; under bursty cart
// mutations (rapid +/-, cross-component fetches) the limit trips and the
// theme would silently fail otherwise. Two retries with backoff covers
// the typical recovery window without delaying real failures by much.
async function brandDrawerResilientFetch(url, init, retries) {
  if (typeof retries !== 'number') retries = 2;
  const backoffs = [1500, 3000];
  for (let attempt = 0; attempt <= retries; attempt++) {
    let response;
    try {
      response = await fetch(url, init);
    } catch (err) {
      if (attempt < retries) {
        await new Promise(r => setTimeout(r, backoffs[attempt]));
        continue;
      }
      throw err;
    }
    if (response.status === 429 && attempt < retries) {
      await new Promise(r => setTimeout(r, backoffs[attempt]));
      continue;
    }
    return response;
  }
}

class BrandCartDrawer {
  constructor() {
    this.config = window.brandCartDrawerConfig || {};
    this.cartData = null;
    this.currentVendor = null;
    this.isOpen = false;
    this.isUpdating = false;

    // Brands the customer has already purchased from (for opening order logic)
    this.purchasedBrands = new Set();

    // Cache DOM elements
    this.drawer = document.getElementById('brand-drawer');
    this.overlay = document.getElementById('brand-drawer-overlay');

    if (!this.drawer || !this.overlay) {
      console.warn('BrandCartDrawer: Required DOM elements not found');
      return;
    }

    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.bindGlobalEvents();
    this.fetchPurchasedBrands();
  }

  /**
   * Fetch purchased brands for opening order logic
   */
  async fetchPurchasedBrands() {
    if (!this.config.customerId) return;

    try {
      const response = await fetch(
        `${this.config.workerUrl}/customer/purchased-brands?customerId=${this.config.customerId}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.purchasedBrands) {
          data.purchasedBrands.forEach(brand => {
            this.purchasedBrands.add(brand.vendor);
          });
        }
      }
    } catch (error) {
      console.error('BrandCartDrawer: Error fetching purchased brands:', error);
    }
  }

  /**
   * Check if this is an opening order (first purchase with this brand)
   */
  isOpeningOrder(vendor) {
    return !this.purchasedBrands.has(vendor);
  }

  /**
   * Get the applicable minimum for a vendor based on whether it's an opening order.
   * Applies cart rules from config.brands[vendor].cartRules when the live cart
   * triggers them. See assets/cart-rule-engine.js for details.
   *
   * Returns { minimum: number (cents), isOpening: boolean, ruleName: string|null }
   */
  getMinimumForVendor(vendor) {
    const brandConfig = this.config.brands?.[vendor] || {};
    const baseOpening = brandConfig.openingMinimum ?? 0;
    const baseMinimum = brandConfig.minimum ?? this.config.defaultMinimum ?? 0;

    const rules = brandConfig.cartRules || [];
    const items = this.cartData?.items || [];

    let effMinimum = baseMinimum;
    let effOpening = baseOpening;
    let matchedRule = null;

    if (rules.length && window.CartRuleEngine) {
      const resolved = window.CartRuleEngine.resolveForVendor({
        rules,
        cartItems: items,
        vendor,
        baseMinimum,
        baseOpening,
      });
      effMinimum = resolved.minimum;
      effOpening = resolved.opening;
      matchedRule = resolved.matchedRule;
    }

    const isOpening = this.isOpeningOrder(vendor);
    if (isOpening && effOpening > 0) {
      return { minimum: effOpening * 100, isOpening: true, ruleName: matchedRule?.name || null };
    }
    return { minimum: effMinimum * 100, isOpening: false, ruleName: matchedRule?.name || null };
  }

  /**
   * Prime the CartRuleEngine tag cache for current cart items. Required before
   * calls to getMinimumForVendor() — rules compare against item tags.
   */
  async primeCartRuleTags() {
    const engine = window.CartRuleEngine;
    if (!engine || !this.cartData?.items?.length) return;
    try {
      await engine.primeTags(this.cartData.items);
    } catch (err) {
      console.warn('[BrandCartDrawer] primeCartRuleTags failed:', err);
    }
  }

  /**
   * Cache frequently accessed DOM elements
   */
  cacheElements() {
    this.elements = {
      // Header
      closeBtn: document.getElementById('brand-drawer-close'),
      icon: document.getElementById('brand-drawer-icon'),
      title: document.getElementById('brand-drawer-title'),
      subtotalHeader: document.getElementById('brand-drawer-subtotal-header'),
      originalHeader: document.getElementById('brand-drawer-original-header'),
      
      // Added banner
      addedBanner: document.getElementById('brand-drawer-added-banner'),
      addedText: document.getElementById('brand-drawer-added-text'),
      
      // Progress
      progress: document.getElementById('brand-drawer-progress'),
      progressText: document.getElementById('brand-drawer-progress-text'),
      progressFill: document.getElementById('brand-drawer-progress-fill'),
      progressCheck: document.getElementById('brand-drawer-progress-check'),
      
      // Badges
      badges: document.getElementById('brand-drawer-badges'),
      tierBadge: document.getElementById('brand-drawer-tier-badge'),
      
      // Body
      body: document.getElementById('brand-drawer-body'),
      items: document.getElementById('brand-drawer-items'),
      loading: document.getElementById('brand-drawer-loading'),
      
      // Footer
      originalPrice: document.getElementById('brand-drawer-original-price'),
      subtotal: document.getElementById('brand-drawer-subtotal'),
      tierSavings: document.getElementById('brand-drawer-tier-savings'),
      savingsAmount: document.getElementById('brand-drawer-savings-amount'),
      continueBtn: document.getElementById('brand-drawer-continue'),
      checkoutBtn: document.getElementById('brand-drawer-checkout'),
      viewAllLink: document.getElementById('brand-drawer-view-all'),
      
      // Template
      itemTemplate: document.getElementById('brand-drawer-item-template')
    };
  }

  /**
   * Bind drawer-specific events
   */
  bindEvents() {
    // Close button
    this.elements.closeBtn?.addEventListener('click', () => this.close());
    
    // Overlay click
    this.overlay?.addEventListener('click', () => this.close());
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Continue shopping
    this.elements.continueBtn?.addEventListener('click', () => this.close());
    
    // Checkout button
    this.elements.checkoutBtn?.addEventListener('click', () => this.handleCheckout());
  }

  /**
   * Bind global events for add-to-cart interception
   */
  bindGlobalEvents() {
    // Listen for cart updates from product forms
    document.addEventListener('cart:item-added', (e) => {
      const { vendor, productTitle } = e.detail || {};
      if (vendor) {
        this.openForVendor(vendor, productTitle);
      }
    });

    // Listen for pubsub cart updates
    if (typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!this.isOpen || !this.currentVendor) return;
        // Skip our own publishes — we already populated post-mutation.
        if (event && event.source === 'brand-cart-drawer') return;
        // If the publisher included the post-mutation cart, render from
        // it directly instead of refetching. This eliminates a /cart.js
        // round-trip per cart-update — critical under storefront throttle
        // when multiple subscribers (OTP manager, product-info,
        // bxgy-promo) all listen for the same event.
        if (event && event.cartData && Array.isArray(event.cartData.items)) {
          this.cartData = event.cartData;
          const vendorItems = this.cartData.items.filter(item => item.vendor === this.currentVendor);
          if (vendorItems.length === 0) {
            this.showEmptyState();
          } else {
            this.populateDrawer(this.currentVendor);
          }
          return;
        }
        this.refreshCart();
      });
    }

    // Listen for clicks on "View cart" buttons in multi-vendor-carts page
    document.addEventListener('click', (e) => {
      const viewCartBtn = e.target.closest('[data-action="view-cart"]');
      if (viewCartBtn) {
        e.preventDefault();
        const vendor = viewCartBtn.dataset.vendor;
        if (vendor) {
          this.openForVendor(vendor);
        }
      }
    });

    // Re-render freebie line steppers once bxgy-promo finishes bootstrap.
    // Without this, an open drawer that rendered BEFORE BxgyPromo was
    // ready stays in fallback-lock mode forever (syncFreeQty doesn't
    // pubsub when delta=0). One-shot listener: bootstrap fires once.
    window.addEventListener('bxgy:ready', () => {
      if (this.isOpen && this.currentVendor && this.cartData) {
        this.populateDrawer(this.currentVendor);
      }
    }, { once: true });
  }

  /**
   * Open drawer for a specific vendor
   * @param {string} vendor - The vendor name
   * @param {string} addedProductTitle - Optional: title of just-added product
   */
  async openForVendor(vendor, addedProductTitle = null) {
    this.currentVendor = vendor;
    this.showLoading(true);
    this.open();

    // Show added banner if product was just added
    if (addedProductTitle) {
      this.showAddedBanner(addedProductTitle);
    } else {
      this.hideAddedBanner();
    }

    await this.fetchCartData();
    this.populateDrawer(vendor);
    // Re-scope the promo card slots to this vendor + nudge the promo
    // modules to re-render. They listen for cart:updated and use
    // data-vendor on each [data-X-promo-card] to filter cart lines.
    this.scopePromoSlotsToVendor(vendor);
    this.showLoading(false);
  }

  /**
   * Update the data-vendor attribute on every promo card skeleton
   * inside the drawer so each promo module re-evaluates against the
   * vendor whose cart is currently displayed. Called whenever the
   * drawer transitions to a new vendor (via openForVendor).
   *
   * Why dispatch cart:updated rather than expose a custom event:
   * the promo modules already debounce-listen for cart:updated to
   * re-render banners; piggy-backing avoids a parallel pubsub channel
   * just for this side-effect-free re-scope.
   */
  scopePromoSlotsToVendor(vendor) {
    if (!this.drawer) return;
    var slots = this.drawer.querySelectorAll('[data-min-qty-promo-card], [data-bundle-promo-card]');
    slots.forEach(function (slot) {
      slot.setAttribute('data-vendor', vendor || '');
    });
    // Tell promo modules to re-render against the new scope.
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: { source: 'brand-cart-drawer:scope' } }),
    );
  }

  /**
   * Open the drawer
   */
  open() {
    if (!this.drawer || !this.overlay) return;

    this.isOpen = true;
    window.brandCartDrawerOpen = true; // Flag to prevent old cart drawer from opening
    this.drawer.classList.add('open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Focus management
    setTimeout(() => {
      this.elements.closeBtn?.focus();
    }, 100);
  }

  /**
   * Close the drawer
   */
  close() {
    if (!this.drawer || !this.overlay) return;

    this.isOpen = false;
    window.brandCartDrawerOpen = false; // Reset flag
    this.drawer.classList.remove('open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('open');
    document.body.style.overflow = '';
    this.currentVendor = null;
    this.hideAddedBanner();
  }

  /**
   * Show loading state
   */
  showLoading(show) {
    if (this.elements.loading) {
      this.elements.loading.classList.toggle('hidden', !show);
    }
    if (this.elements.items) {
      this.elements.items.style.opacity = show ? '0.5' : '1';
    }
  }

  /**
   * Show the "item added" banner
   */
  showAddedBanner(productTitle) {
    if (this.elements.addedBanner && this.elements.addedText) {
      const text = productTitle 
        ? `"${productTitle}" added to cart` 
        : this.config.strings?.itemAdded || 'Item added to cart';
      this.elements.addedText.textContent = text;
      this.elements.addedBanner.classList.add('show');

      // Auto-hide after 4 seconds
      setTimeout(() => {
        this.hideAddedBanner();
      }, 4000);
    }
  }

  /**
   * Hide the "item added" banner
   */
  hideAddedBanner() {
    if (this.elements.addedBanner) {
      this.elements.addedBanner.classList.remove('show');
    }
  }

  /**
   * Fetch current cart data — retry-aware. Preserves last good cartData
   * on terminal failure so the drawer never blanks out under throttle.
   */
  async fetchCartData() {
    try {
      const response = await brandDrawerResilientFetch(`${this.config.routes?.cartUrl || '/cart'}.js`);
      if (!response || !response.ok) {
        throw new Error(`fetchCartData ${response ? response.status : 'no-response'}`);
      }
      this.cartData = await response.json();
      await this.primeCartRuleTags();
      return this.cartData;
    } catch (error) {
      console.error('BrandCartDrawer: Error fetching cart:', error);
      return null;
    }
  }

  /**
   * Refresh cart data and UI
   */
  async refreshCart() {
    if (!this.currentVendor || this.isUpdating) return;
    await this.fetchCartData();
    this.populateDrawer(this.currentVendor);
  }

  /**
   * Populate the drawer with vendor's cart data
   */
  populateDrawer(vendor) {
    if (!this.cartData) return;

    const vendorItems = this.cartData.items.filter(item => item.vendor === vendor);
    const brandConfig = this.config.brands?.[vendor] || {};
    const { minimum, isOpening } = this.getMinimumForVendor(vendor);
    const subtotal = vendorItems.reduce((sum, item) => sum + item.final_line_price, 0);
    const hasMinimum = minimum > 0;
    const progress = hasMinimum ? Math.min((subtotal / minimum) * 100, 100) : 100;
    const minimumReached = !hasMinimum || subtotal >= minimum;
    const amountToGo = Math.max(minimum - subtotal, 0);

    // Calculate tier based on cart subtotal
    const tierInfo = this.calculateTierFromSubtotal(vendor, subtotal);

    // Check if Shopify discounts are already applied for this vendor
    // If so, use original_line_price for strikethrough instead of recalculating tier %
    const hasShopifyDiscount = vendorItems.some(item =>
      (item.line_level_discount_allocations || []).some(d => d.amount > 0)
    );
    const originalSubtotal = hasShopifyDiscount
      ? vendorItems.reduce((sum, item) => sum + item.original_line_price, 0)
      : 0;

    // Update header (now includes tier pricing)
    this.updateHeader(vendor, subtotal, brandConfig, tierInfo, hasShopifyDiscount, originalSubtotal);

    // Update progress
    this.updateProgress(hasMinimum, minimumReached, minimum, amountToGo, progress, isOpening);

    // Update badges (tier info)
    this.updateBadges(tierInfo);

    // Populate items
    this.populateItems(vendorItems);

    // Mix notice — show when this vendor has BXGY freebies in cart
    this.updateMixNotice(vendorItems);

    // Update footer (now includes tier pricing)
    this.updateFooter(subtotal, minimumReached, tierInfo, hasShopifyDiscount, originalSubtotal);

    // Update tier progress bar
    this.updateTierProgress(subtotal, tierInfo);

    // Check if no items (cart was emptied)
    if (vendorItems.length === 0) {
      this.showEmptyState();
    }
  }

  /**
   * Calculate tier based on cart subtotal
   */
  calculateTierFromSubtotal(vendor, subtotal) {
    // Try multiple config sources for brand tiers (drawer config, or multi-vendor config)
    const brandTiersConfig = this.config.brandSpendTiers?.[vendor] 
      || window.multiVendorCartsConfig?.brandSpendTiers?.[vendor];
    
    if (!brandTiersConfig || !brandTiersConfig.enabled || !brandTiersConfig.tiers?.length) {
      return null;
    }

    const tiers = brandTiersConfig.tiers;
    
    // Sort tiers by minSpend descending
    const sortedTiers = [...tiers].sort((a, b) => b.minSpend - a.minSpend);
    
    // Subtotal in dollars
    const subtotalDollars = subtotal / 100;
    
    // Find current tier
    let currentTier = null;
    for (const tier of sortedTiers) {
      if (subtotalDollars >= tier.minSpend) {
        currentTier = {
          name: tier.name,
          threshold: tier.minSpend,
          discount: tier.discount
        };
        break;
      }
    }
    
    // Find next tier
    let nextTier = null;
    const sortedAscending = [...tiers].sort((a, b) => a.minSpend - b.minSpend);
    for (const tier of sortedAscending) {
      if (subtotalDollars < tier.minSpend) {
        nextTier = {
          name: tier.name,
          threshold: tier.minSpend,
          discount: tier.discount
        };
        break;
      }
    }

    return {
      currentTier,
      nextTier,
      subtotalDollars
    };
  }

  /**
   * Clean tier name by removing brand prefix
   */
  cleanTierName(tierName) {
    if (!tierName) return '';
    // Remove patterns like "Brand - ", "Brand: ", "Brand– "
    return tierName.replace(/^[^-:–]+[-:–]\s*/i, '').trim();
  }

  /**
   * Get tier color based on tier name
   */
  getTierColor(tierName) {
    // Use neutral theme-consistent color for all tiers
    return 'var(--bcd-text, #1a1a1a)';
  }

  /**
   * Update drawer header
   */
  updateHeader(vendor, subtotal, brandConfig, tierInfo, hasShopifyDiscount = false, originalSubtotal = 0) {
    // If there's a logo, show only the logo. Otherwise show only the name.
    if (this.elements.icon && this.elements.title) {
      if (brandConfig.icon) {
        // Has logo - show logo, hide title
        this.elements.icon.innerHTML = `<img src="${brandConfig.icon}" alt="${vendor}" width="40" height="40">`;
        this.elements.icon.classList.remove('hidden');
        this.elements.title.classList.add('hidden');
      } else {
        // No logo - hide icon area, show title
        this.elements.icon.innerHTML = '';
        this.elements.icon.classList.add('hidden');
        this.elements.title.textContent = vendor;
        this.elements.title.classList.remove('hidden');
      }
    }

    // Subtotal header with tier pricing
    this.updateHeaderPricing(subtotal, tierInfo, hasShopifyDiscount, originalSubtotal);
  }

  /**
   * Update header pricing (original + discounted)
   * When hasShopifyDiscount is true, show Shopify's original_line_price as strikethrough
   * and final_line_price as the current price (already discounted by Shopify).
   * When no Shopify discount, calculate tier % visually as before.
   */
  updateHeaderPricing(subtotal, tierInfo, hasShopifyDiscount = false, originalSubtotal = 0) {
    const hasTier = tierInfo?.currentTier;
    const discount = hasTier ? tierInfo.currentTier.discount : 0;

    let showStrikethrough = false;
    let strikethroughAmount = 0;
    let displaySubtotal = subtotal;

    if (hasShopifyDiscount && originalSubtotal > subtotal) {
      // Shopify discount: show original price strikethrough, final price as current
      showStrikethrough = true;
      strikethroughAmount = originalSubtotal;
      displaySubtotal = subtotal;
    } else if (hasTier && discount > 0) {
      // No Shopify discount: calculate tier % visually
      showStrikethrough = true;
      strikethroughAmount = subtotal;
      displaySubtotal = Math.round(subtotal * (1 - discount / 100));
    }

    // Original price (strikethrough)
    if (this.elements.originalHeader) {
      if (showStrikethrough) {
        this.elements.originalHeader.textContent = 'WSP ' + this.formatMoney(strikethroughAmount);
        this.elements.originalHeader.classList.remove('hidden');
      } else {
        this.elements.originalHeader.classList.add('hidden');
      }
    }

    // Current/discounted price
    if (this.elements.subtotalHeader) {
      this.elements.subtotalHeader.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(displaySubtotal);
    }
  }

  /**
   * Update progress bar
   */
  updateProgress(hasMinimum, minimumReached, minimum, amountToGo, progress, isOpening = false) {
    if (!this.elements.progress) return;

    if (!hasMinimum) {
      this.elements.progress.classList.add('hidden');
      return;
    }

    this.elements.progress.classList.remove('hidden');
    const minimumLabel = isOpening ? 'Opening order' : 'Minimum order';

    // Progress text
    if (this.elements.progressText) {
      if (minimumReached) {
        this.elements.progressText.innerHTML = `<span class="progress-complete">${this.config.strings?.minimumReached || 'Minimum reached'}</span>`;
      } else {
        this.elements.progressText.textContent = `${minimumLabel}: ${this.formatMoney(minimum)} • ${this.formatMoney(amountToGo)} ${this.config.strings?.toGo || 'to go'}`;
      }
    }

    // Progress fill
    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = `${progress}%`;
      this.elements.progressFill.classList.toggle('complete', minimumReached);
    }

    // Progress check icon
    if (this.elements.progressCheck) {
      this.elements.progressCheck.classList.toggle('complete', minimumReached);
    }
  }

  /**
   * Update badges (tier)
   */
  updateBadges(tierInfo) {
    const hasTier = tierInfo?.currentTier;

    if (this.elements.badges) {
      this.elements.badges.classList.toggle('hidden', !hasTier);
    }

    // Tier badge
    if (this.elements.tierBadge) {
      if (hasTier) {
        const cleanName = this.cleanTierName(tierInfo.currentTier.name);
        const discount = tierInfo.currentTier.discount;

        this.elements.tierBadge.innerHTML = `${discount}% OFF`;
        this.elements.tierBadge.title = `${cleanName} tier discount`;
        this.elements.tierBadge.classList.remove('hidden');
      } else {
        this.elements.tierBadge.classList.add('hidden');
      }
    }
  }

  /**
   * Toggle the BXGY mix-split notice in the drawer header. The drawer is
   * brand-scoped, so we only consider this vendor's items when deciding
   * whether the notice applies. The notice itself just routes the user
   * to /pages/carts where the mix editor lives — drawer doesn't surface
   * the editor because the freebies render as locked line items here.
   */
  updateMixNotice(vendorItems) {
    const notice = document.getElementById('brand-drawer-mix-notice');
    if (!notice) return;
    const hasPromoFree = vendorItems.some(item =>
      item.properties && item.properties._marini_q2_free === '1'
    );
    if (hasPromoFree) notice.classList.remove('hidden');
    else notice.classList.add('hidden');
  }

  /**
   * Populate cart items
   */
  populateItems(vendorItems) {
    if (!this.elements.items || !this.elements.itemTemplate) return;

    this.elements.items.innerHTML = '';

    vendorItems.forEach(item => {
      const itemEl = this.elements.itemTemplate.content.cloneNode(true);
      const container = itemEl.querySelector('.brand-drawer-item');

      container.dataset.lineKey = item.key;
      container.dataset.variantId = item.variant_id;

      // Promo-free flag: lines added by bxgy-promo.js carry
      // properties._marini_q2_free='1'. They render as locked/system-managed
      // (qty +/-/remove disabled) since the qty is auto-balanced against the
      // qualifying SCMS count by syncFreeQty() — manual edits would just be
      // re-added. The mix editor on /pages/carts is the editing surface.
      const isPromoFree = item.properties && item.properties._marini_q2_free === '1';
      if (isPromoFree) container.classList.add('brand-drawer-item--promo-free');

      // Image
      const img = itemEl.querySelector('img');
      const imgLink = itemEl.querySelector('.brand-drawer-item-image-link');
      if (item.image) {
        img.src = this.getSizedImageUrl(item.image, '160x160');
        img.alt = item.title;
      } else {
        img.parentElement.innerHTML = `<div class="brand-drawer-item-placeholder">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
            <circle cx="9" cy="9" r="2"></circle>
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
          </svg>
        </div>`;
      }
      if (imgLink) imgLink.href = item.url;

      // Title and details
      const titleEl = itemEl.querySelector('.brand-drawer-item-title');
      titleEl.textContent = item.product_title;
      titleEl.href = item.url;

      itemEl.querySelector('.brand-drawer-item-variant').textContent = item.variant_title || '';
      itemEl.querySelector('.brand-drawer-item-price').innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(item.price);

      // Check if this is a one-time purchase product
      const isOneTimePurchase = item.properties && item.properties._one_time_purchase === 'true';

      // Force quantity to 1 for one-time purchase products
      if (isOneTimePurchase && item.quantity > 1) {
        // Update quantity to 1
        this.updateItemQuantity(item.key, 1);
        itemEl.querySelector('.brand-drawer-qty-input').value = 1;
      } else {
        itemEl.querySelector('.brand-drawer-qty-input').value = item.quantity;
      }

      // Per-line pricing.
      //
      // Shopify exposes two relevant fields on /cart.js items:
      //   - original_line_price: pre-discount line total (price × qty)
      //   - final_line_price:    post-discount line total
      //
      // When a Shopify discount has fired (automatic or app-Function),
      // the gap is non-zero — surface the original strikethrough so
      // practitioners SEE the saving in the drawer rather than only at
      // checkout. This is what was missing per Manuel's feedback ("no
      // se ven los descuentos … precio anterior tachado").
      var linePriceEl = itemEl.querySelector('.brand-drawer-item-line-price');
      if (linePriceEl) {
        var originalLinePrice = item.original_line_price;
        var finalLinePrice = item.final_line_price;
        var hasLineDiscount =
          typeof originalLinePrice === 'number' &&
          typeof finalLinePrice === 'number' &&
          originalLinePrice > finalLinePrice;
        if (hasLineDiscount) {
          // Strikethrough WSP + final WSP + tiny savings tag.
          var savedAmount = originalLinePrice - finalLinePrice;
          var pct = Math.round((savedAmount / originalLinePrice) * 100);
          linePriceEl.innerHTML =
            '<span class="brand-drawer-item-line-price-original">' +
              '<span class="brand-drawer-wsp-label">WSP</span> ' +
              this.formatMoney(originalLinePrice) +
            '</span>' +
            '<span class="brand-drawer-item-line-price-final">' +
              '<span class="brand-drawer-wsp-label">WSP</span> ' +
              this.formatMoney(finalLinePrice) +
            '</span>' +
            '<span class="brand-drawer-item-line-price-saving">−' + pct + '%</span>';
          itemEl.querySelector('.brand-drawer-item-line-total').classList.add('brand-drawer-item-line-total--discounted');
        } else {
          linePriceEl.innerHTML =
            '<span class="brand-drawer-wsp-label">WSP</span> ' +
            this.formatMoney(finalLinePrice);
        }
      }

      // Bind quantity controls
      this.bindItemControls(itemEl, item);

      this.elements.items.appendChild(itemEl);
    });
  }

  /**
   * Bind quantity and remove controls for an item
   */
  bindItemControls(itemEl, item) {
    const decreaseBtn = itemEl.querySelector('[data-action="decrease"]');
    const increaseBtn = itemEl.querySelector('[data-action="increase"]');
    const removeBtn = itemEl.querySelector('[data-action="remove"]');
    const qtyInput = itemEl.querySelector('.brand-drawer-qty-input');

    // Promo-free lines (bxgy 1:1 freebies) — qty +/− rebalance the
    // Serum/Cream split via BxgyPromo.rebalance(). The total stays equal
    // to the qualifying SCMS count (Shopify's Function enforces the
    // discount cap regardless), so each click is a delta-pair under the
    // hood: −1 from one slot, +1 to the other. The remove button stays
    // disabled — removing a freebie line entirely would break the
    // entitlement and the auto-add would just re-create the line.
    //
    // The qty input is read-only (typing 8 into a 6-entitlement total
    // doesn't have a sensible delta-pair interpretation). Customer
    // adjusts the split via the +/− buttons only.
    const isPromoFree = item.properties && item.properties._marini_q2_free === '1';
    if (isPromoFree) {
      const slot = (window.BxgyPromo && window.BxgyPromo.isReady())
        ? window.BxgyPromo.getSlotForProduct(item.product_id)
        : null;
      // If BxgyPromo isn't ready (race condition during script load),
      // fall back to the old lock behavior. The next cart:updated will
      // re-render with BxgyPromo ready and unlock the steppers.
      if (!slot) {
        [decreaseBtn, increaseBtn, qtyInput, removeBtn].forEach(el => {
          if (el) {
            el.disabled = true;
            el.style.opacity = '0.4';
            el.style.cursor = 'not-allowed';
          }
        });
        return;
      }
      // Mark the line so CSS can style freebie steppers distinctly if
      // we ever want to (e.g. accent color). Pure metadata for now.
      const itemContainer = itemEl.querySelector('.brand-drawer-item');
      if (itemContainer) {
        itemContainer.dataset.bxgySlot = slot;
        itemContainer.classList.add('brand-drawer-item--bxgy-mix');
      }
      // Qty input read-only — typing breaks the delta-pair semantics.
      if (qtyInput) {
        qtyInput.readOnly = true;
        qtyInput.style.cursor = 'default';
      }
      // Remove stays disabled (removing destroys the freebie line —
      // auto-add would just re-add it on the next cart event).
      if (removeBtn) {
        removeBtn.disabled = true;
        removeBtn.style.opacity = '0.4';
        removeBtn.style.cursor = 'not-allowed';
        removeBtn.title = 'Free with your SCMS — adjust the split with +/− instead.';
      }
      // Bind the +/− to BxgyPromo.rebalance.
      // Optimistic UI: update qty input + sibling line's input, disable
      // buttons until the promise settles.
      const callRebalance = (sign) => {
        if (decreaseBtn?.disabled || increaseBtn?.disabled) return;
        const owned = window.BxgyPromo.getOwned();
        // Bail if the move would push the slot outside [0, total].
        if (sign === -1 && owned[slot] < 1) return;
        if (sign === 1) {
          const otherSlot = slot === 'serum' ? 'cream' : 'serum';
          if (owned[otherSlot] < 1) return;
        }
        // Optimistic stepper value for THIS line.
        if (qtyInput) qtyInput.value = String(owned[slot] + sign);
        // Lock both freebie lines' steppers across the drawer until
        // the rebalance completes (prevents racing clicks).
        const allFreebieBtns = this.elements.items.querySelectorAll(
          '.brand-drawer-item--bxgy-mix .brand-drawer-qty-btn'
        );
        allFreebieBtns.forEach(b => {
          b.disabled = true;
          b.style.opacity = '0.4';
        });
        window.BxgyPromo.rebalance(slot, sign).catch(err => {
          console.warn('[brand-cart-drawer] rebalance failed:', err);
          // The drawer will re-render from cart:updated (rebalance
          // pubsubs on success, our own listener handles failure
          // rollback by re-fetching cart). If render doesn't happen
          // within 1s, force a refetch + repopulate.
          setTimeout(() => {
            allFreebieBtns.forEach(b => {
              b.disabled = false;
              b.style.opacity = '';
            });
          }, 1000);
        });
      };
      decreaseBtn?.addEventListener('click', () => callRebalance(-1));
      increaseBtn?.addEventListener('click', () => callRebalance(1));
      return;
    }

    // Check if this is a one-time purchase product
    const isOneTimePurchase = item.properties && item.properties._one_time_purchase === 'true';

    if (isOneTimePurchase) {
      // Disable quantity controls for one-time purchase products
      if (decreaseBtn) {
        decreaseBtn.disabled = true;
        decreaseBtn.style.opacity = '0.5';
        decreaseBtn.style.cursor = 'not-allowed';
      }
      if (increaseBtn) {
        increaseBtn.disabled = true;
        increaseBtn.style.opacity = '0.5';
        increaseBtn.style.cursor = 'not-allowed';
      }
      if (qtyInput) {
        qtyInput.disabled = true;
        qtyInput.style.opacity = '0.7';
        qtyInput.style.cursor = 'not-allowed';
      }

      // Keep remove button enabled
      removeBtn?.addEventListener('click', () => {
        this.updateItemQuantity(item.key, 0);
      });

      return; // Don't bind the quantity change listeners
    }

    // Normal behavior for regular products — disable decrease when at MOQ.
    // Remove button stays enabled (MOQ allows qty=0 removal).
    const moq = (window.__moqGuard && window.__moqGuard.getMOQ)
      ? window.__moqGuard.getMOQ(item.variant_id)
      : 1;
    const atMoq = item.quantity <= moq && moq > 1;
    if (decreaseBtn) {
      if (atMoq) {
        decreaseBtn.disabled = true;
        decreaseBtn.style.opacity = '0.4';
        decreaseBtn.style.cursor = 'not-allowed';
        decreaseBtn.title = `Minimum order quantity is ${moq}. Use the trash icon to remove.`;
      } else {
        decreaseBtn.disabled = false;
        decreaseBtn.style.opacity = '';
        decreaseBtn.style.cursor = '';
        decreaseBtn.removeAttribute('title');
      }
    }
    if (qtyInput) qtyInput.min = moq > 1 ? moq : 0;

    decreaseBtn?.addEventListener('click', () => {
      if (decreaseBtn.disabled) return;
      this.updateItemQuantity(item.key, Math.max(0, item.quantity - 1));
    });

    increaseBtn?.addEventListener('click', () => {
      this.updateItemQuantity(item.key, item.quantity + 1);
    });

    removeBtn?.addEventListener('click', () => {
      this.updateItemQuantity(item.key, 0);
    });

    qtyInput?.addEventListener('change', (e) => {
      const newQty = parseInt(e.target.value) || 0;
      this.updateItemQuantity(item.key, newQty);
    });
  }

  /**
   * Update item quantity
   */
  async updateItemQuantity(lineKey, quantity) {
    if (this.isUpdating) return;
    this.isUpdating = true;
    this.showLoading(true);

    try {
      const response = await brandDrawerResilientFetch(
        `${this.config.routes?.cartChangeUrl || '/cart/change'}.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: quantity
          })
        }
      );

      if (!response || !response.ok) {
        throw new Error(`HTTP error! status: ${response ? response.status : 'no-response'}`);
      }

      this.cartData = await response.json();
      await this.primeCartRuleTags();

      // Check if vendor still has items
      const vendorItems = this.cartData.items.filter(item => item.vendor === this.currentVendor);
      if (vendorItems.length === 0) {
        this.showEmptyState();
      } else {
        this.populateDrawer(this.currentVendor);
      }

      // Update cart icon bubble
      this.updateCartBubble();

      // Dispatch event for other components
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: this.cartData }));

      // Also publish to pubsub if available
      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-cart-drawer', cartData: this.cartData });
      }

    } catch (error) {
      console.error('BrandCartDrawer: Error updating quantity:', error);
    } finally {
      this.isUpdating = false;
      this.showLoading(false);
    }
  }

  /**
   * Update footer with totals
   * When hasShopifyDiscount is true, show Shopify's original_line_price as strikethrough.
   * When no Shopify discount, calculate tier % visually as before.
   */
  updateFooter(subtotal, minimumReached, tierInfo, hasShopifyDiscount = false, originalSubtotal = 0) {
    const hasTier = tierInfo?.currentTier;
    const discount = hasTier ? tierInfo.currentTier.discount : 0;

    let showStrikethrough = false;
    let strikethroughAmount = 0;
    let displaySubtotal = subtotal;
    let savingsAmount = 0;

    if (hasShopifyDiscount && originalSubtotal > subtotal) {
      // Shopify discount: show original price strikethrough, final price as current
      showStrikethrough = true;
      strikethroughAmount = originalSubtotal;
      displaySubtotal = subtotal;
      savingsAmount = originalSubtotal - subtotal;
    } else if (hasTier && discount > 0) {
      // No Shopify discount: calculate tier % visually
      showStrikethrough = true;
      strikethroughAmount = subtotal;
      displaySubtotal = Math.round(subtotal * (1 - discount / 100));
      savingsAmount = subtotal - displaySubtotal;
    }

    // Show original price strikethrough
    if (this.elements.originalPrice) {
      if (showStrikethrough) {
        this.elements.originalPrice.textContent = 'WSP ' + this.formatMoney(strikethroughAmount);
        this.elements.originalPrice.classList.remove('hidden');
      } else {
        this.elements.originalPrice.classList.add('hidden');
      }
    }

    // Subtotal
    if (this.elements.subtotal) {
      this.elements.subtotal.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(displaySubtotal);
    }

    // Tier savings section
    if (this.elements.tierSavings) {
      if (showStrikethrough && savingsAmount > 0) {
        this.elements.tierSavings.classList.remove('hidden');
        if (this.elements.savingsAmount) {
          this.elements.savingsAmount.textContent = this.formatMoney(savingsAmount);
        }
      } else {
        this.elements.tierSavings.classList.add('hidden');
      }
    }

    // Checkout button
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = !minimumReached;
    }
  }

  /**
   * Update tier progress bar in drawer
   */
  updateTierProgress(subtotal, tierInfo) {
    const progressContainer = document.getElementById('brand-drawer-tier-progress');
    
    if (!progressContainer) return;

    // If no tier data or at max tier, hide the progress
    if (!tierInfo || !tierInfo.nextTier) {
      progressContainer.classList.add('hidden');
      return;
    }

    const { nextTier, subtotalDollars } = tierInfo;
    const amountNeeded = nextTier.threshold - subtotalDollars;
    const cleanName = this.cleanTierName(nextTier.name);
    const color = this.getTierColor(nextTier.name);

    // Show the progress
    progressContainer.classList.remove('hidden');
    progressContainer.innerHTML = `
      <div class="brand-drawer-tier-progress-content">
        <div class="tier-progress-text">
          Spend <strong>${this.formatMoney(amountNeeded * 100)}</strong> more for 
          <strong>${cleanName}</strong> (${nextTier.discount}% off)
        </div>
        <div class="tier-progress-bar">
          <div class="tier-progress-fill" style="width: ${Math.min((subtotalDollars / nextTier.threshold) * 100, 100)}%"></div>
        </div>
      </div>
    `;
  }

  /**
   * Show empty state when no items
   */
  showEmptyState() {
    if (this.elements.items) {
      this.elements.items.innerHTML = `
        <div class="brand-drawer-empty">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <circle cx="8" cy="21" r="1"></circle>
            <circle cx="19" cy="21" r="1"></circle>
            <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
          </svg>
          <p>No items from this brand in your cart</p>
        </div>
      `;
    }

    // Disable checkout
    if (this.elements.checkoutBtn) {
      this.elements.checkoutBtn.disabled = true;
    }

    // Hide progress
    if (this.elements.progress) {
      this.elements.progress.classList.add('hidden');
    }

    // Update header subtotal
    if (this.elements.subtotalHeader) {
      this.elements.subtotalHeader.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(0);
    }
    if (this.elements.originalHeader) {
      this.elements.originalHeader.classList.add('hidden');
    }
    if (this.elements.subtotal) {
      this.elements.subtotal.innerHTML = '<span class="brand-drawer-wsp-label">WSP</span> ' + this.formatMoney(0);
    }

    // Hide tier elements
    if (this.elements.tierBadge) {
      this.elements.tierBadge.classList.add('hidden');
    }
    if (this.elements.badges) {
      this.elements.badges.classList.add('hidden');
    }
    if (this.elements.originalPrice) {
      this.elements.originalPrice.classList.add('hidden');
    }
    if (this.elements.tierSavings) {
      this.elements.tierSavings.classList.add('hidden');
    }
    const tierProgress = document.getElementById('brand-drawer-tier-progress');
    if (tierProgress) {
      tierProgress.classList.add('hidden');
    }
  }

  /**
   * Handle checkout for current vendor
   */
  async handleCheckout() {
    if (!this.currentVendor || !this.cartData) return;

    const vendorItems = this.cartData.items.filter(item => item.vendor === this.currentVendor);
    const otherItems = this.cartData.items.filter(item => item.vendor !== this.currentVendor);

    if (vendorItems.length === 0) return;

    // Check minimum
    const { minimum, isOpening } = this.getMinimumForVendor(this.currentVendor);
    const subtotal = vendorItems.reduce((sum, item) => sum + item.final_line_price, 0);

    if (minimum > 0 && subtotal < minimum) {
      const minType = isOpening ? 'opening order' : 'minimum order';
      alert(`Please add ${this.formatMoney(minimum - subtotal)} more to meet the ${minType} for ${this.currentVendor}.`);
      return;
    }

    // Per-item MOQ check — block checkout if any line item is below variant's custom.moq
    const moqGet = (window.__moqGuard && window.__moqGuard.getMOQ) || ((id) => (window.__MOQ_MAP__ && window.__MOQ_MAP__[id]) || 1);
    const moqViolations = vendorItems
      .map((item) => ({ item, moq: moqGet(item.variant_id) }))
      .filter(({ item, moq }) => item.quantity < moq);
    if (moqViolations.length > 0) {
      const lines = moqViolations.map(({ item, moq }) => `\u2022 ${item.product_title}: ${item.quantity} (need ${moq})`).join('\n');
      alert(`Cannot checkout ${this.currentVendor}. Minimum order quantity not met:\n\n${lines}`);
      return;
    }

    // If there are other items, park them
    if (otherItems.length > 0) {
      await this.parkItems(otherItems);
      await this.removeItemsFromCart(otherItems);

      // Set flags for restoration
      sessionStorage.setItem('bcd_checkout_vendor', this.currentVendor);
      sessionStorage.setItem('bcd_parked_items', JSON.stringify(otherItems.map(item => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties
      }))));
    }

    // Redirect to checkout
    window.location.href = this.config.routes?.checkoutUrl || '/checkout';
  }

  /**
   * Park items (save for later restoration)
   */
  async parkItems(items) {
    if (!this.config.customerId || items.length === 0) {
      return; // For guests, sessionStorage is enough
    }

    try {
      await fetch(`${this.config.workerUrl}/cart/park`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: this.config.customerId,
          items: items.map(item => ({
            id: item.variant_id,
            quantity: item.quantity,
            properties: item.properties
          }))
        })
      });
    } catch (error) {
      console.error('BrandCartDrawer: Error parking items:', error);
    }
  }

  /**
   * Remove items from cart
   */
  async removeItemsFromCart(items) {
    const updates = {};
    items.forEach(item => {
      updates[item.key] = 0;
    });

    try {
      const response = await brandDrawerResilientFetch(
        `${this.config.routes?.cartUpdateUrl || '/cart/update'}.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ updates })
        }
      );
      if (!response || !response.ok) {
        throw new Error(`removeItemsFromCart ${response ? response.status : 'no-response'}`);
      }
    } catch (error) {
      console.error('BrandCartDrawer: Error removing items:', error);
    }
  }

  /**
   * Update cart icon bubble
   */
  async updateCartBubble() {
    try {
      const response = await brandDrawerResilientFetch(`${this.config.routes?.cartUrl || '/cart'}?section_id=cart-icon-bubble`);
      if (!response || !response.ok) return;
      const html = await response.text();
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newBubble = doc.querySelector('.shopify-section');
        if (newBubble) {
          bubbleEl.innerHTML = newBubble.innerHTML;
        }
      }
    } catch (error) {
      console.error('BrandCartDrawer: Error updating cart bubble:', error);
    }
  }

  /**
   * Format money according to shop settings
   */
  formatMoney(cents) {
    const amount = cents / 100;
    // Use cart data currency (actual presentment currency) over config
    const currency = this.cartData?.currency || this.config.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  /**
   * Get sized image URL
   */
  getSizedImageUrl(url, size) {
    if (!url) return '';
    return url
      .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)+\./g, '.')
      .replace(/\.([^.]+)$/, `_${size}.$1`);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.brandCartDrawer = new BrandCartDrawer();
  });
} else {
  window.brandCartDrawer = new BrandCartDrawer();
}

/**
 * Global function to open the brand cart drawer
 * Can be called from anywhere: window.openBrandCartDrawer('Vendor Name', 'Product Title')
 */
window.openBrandCartDrawer = function(vendor, productTitle = null) {
  if (window.brandCartDrawer) {
    window.brandCartDrawer.openForVendor(vendor, productTitle);
  }
};
